import React, { useEffect, useRef, useState } from "react";
import { sessionService } from "../../services/sessionService";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { SystemNotificationData, ParticipantsListData } from "../../types/websocket";

// A connected user as maintained by this panel
interface ConnectedUser {
  userId: string;
  username: string;
  characterId?: string;
  characterName?: string;
  isDM: boolean;
  joinedAt: string;
}

interface ParticipantPanelProps {
  sessionId: string;
  className?: string;
}

export const ParticipantPanel: React.FC<ParticipantPanelProps> = ({
  sessionId,
  className = "",
}) => {
  const { lastMessage } = useWebSocketContext();
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track whether we have received authoritative WS data so we don't overwrite it
  const wsInitializedRef = useRef(false);

  // Initial load: fetch currently-online participants from REST API as a quick seed.
  // Once the participants_list WS message arrives it will replace this data.
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const response = await sessionService.getSessionParticipants(sessionId);
        // Only show participants already connected; WS events will keep this live
        if (!wsInitializedRef.current) {
          const online: ConnectedUser[] = response.participants
            .filter((p) => p.is_online)
            .map((p) => ({
              userId: String(p.user_id),
              username: p.username,
              characterId: p.character_id != null ? String(p.character_id) : undefined,
              characterName: p.character_name ?? undefined,
              isDM: p.is_dm,
              joinedAt: p.joined_at,
            }));
          setConnectedUsers(online);
          setError(null);
        }
      } catch (err) {
        if (!wsInitializedRef.current) {
          setError(err instanceof Error ? err.message : "Failed to load participants");
        }
      } finally {
        if (!wsInitializedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchInitial();
  }, [sessionId]);

  // React to WebSocket events: participants_list, user_joined, user_left
  useEffect(() => {
    if (!lastMessage) return;
    if (String(lastMessage.sessionId) !== String(sessionId)) return;

    if (lastMessage.type === "participants_list") {
      const data = lastMessage.data as ParticipantsListData;
      if (data?.participants) {
        wsInitializedRef.current = true;
        setConnectedUsers(
          data.participants.map((p) => ({
            userId: p.userId,
            username: p.username,
            characterId: p.characterId,
            characterName: p.characterName,
            isDM: p.isDM,
            joinedAt: lastMessage.timestamp,
          }))
        );
        setLoading(false);
        setError(null);
      }
    } else if (lastMessage.type === "user_joined") {
      const data = lastMessage.data as SystemNotificationData;
      if (data?.userId) {
        const joined: ConnectedUser = {
          userId: data.userId,
          username: data.username ?? data.userId,
          characterId: data.characterId,
          characterName: data.characterName,
          isDM: data.isDM ?? false,
          joinedAt: lastMessage.timestamp,
        };
        setConnectedUsers((prev) => {
          // Avoid duplicates – update character info if the user is already listed
          const exists = prev.find((u) => u.userId === joined.userId);
          if (exists) {
            return prev.map((u) => (u.userId === joined.userId ? { ...u, ...joined } : u));
          }
          return [...prev, joined];
        });
      }
    } else if (lastMessage.type === "user_left") {
      const data = lastMessage.data as SystemNotificationData;
      if (data?.userId) {
        setConnectedUsers((prev) => prev.filter((u) => u.userId !== data.userId));
      }
    }
  }, [lastMessage, sessionId]);

  const formatJoinTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className={`bg-gray-800 rounded-lg border border-gray-600 p-4 ${className}`}>
        <h3 className="text-white font-semibold text-lg mb-4">Players</h3>
        <div className="text-gray-400 text-center">Loading participants...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-gray-800 rounded-lg border border-gray-600 p-4 ${className}`}>
        <h3 className="text-white font-semibold text-lg mb-4">Players</h3>
        <div className="text-red-400 text-center">{error}</div>
      </div>
    );
  }

  return (
    <div
      className={`bg-gray-800 rounded-lg border border-gray-600 p-4 ${className}`}
      data-testid="participant-panel"
      role="region"
      aria-label="Session participants"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg">Players</h3>
        <span
          className="text-gray-400 text-sm"
          data-testid="participant-count"
          aria-label={`${connectedUsers.length} participants connected`}
        >
          {connectedUsers.length} connected
        </span>
      </div>

      <div className="space-y-3" data-testid="participant-list">
        {connectedUsers.map((user) => (
          <div
            key={user.userId}
            className="flex items-center space-x-3 p-2 rounded-lg bg-gray-700 hover:bg-gray-650 transition-colors"
            data-testid={`participant-${user.userId}`}
            role="listitem"
            aria-label={`${user.characterName || user.username} - ${user.isDM ? "Dungeon Master" : "Player"} - Online`}
          >
            {/* Avatar with online indicator */}
            <div className="relative" data-testid={`participant-avatar-${user.userId}`}>
              <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {(user.characterName || user.username)[0].toUpperCase()}
                </span>
              </div>
              {/* All displayed users are connected */}
              <div
                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-gray-700 bg-green-500"
                data-testid={`participant-status-${user.userId}`}
                aria-label="Online"
              />
            </div>

            {/* Participant info */}
            <div className="flex-1 min-w-0" data-testid={`participant-info-${user.userId}`}>
              <div className="flex items-center space-x-2">
                <span
                  className="text-white font-medium truncate"
                  data-testid={`participant-name-${user.userId}`}
                >
                  {user.characterName || user.username}
                </span>
                {user.isDM && (
                  <span
                    className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full"
                    data-testid={`participant-dm-badge-${user.userId}`}
                  >
                    DM
                  </span>
                )}
              </div>
              <div
                className="text-gray-400 text-sm truncate"
                data-testid={`participant-username-${user.userId}`}
              >
                {user.characterName ? user.username : "No character"}
              </div>
            </div>

            {/* Join time */}
            <div
              className="text-gray-400 text-xs"
              data-testid={`participant-join-time-${user.userId}`}
            >
              {formatJoinTime(user.joinedAt)}
            </div>
          </div>
        ))}

        {connectedUsers.length === 0 && (
          <div className="text-gray-500 text-center text-sm py-4">No players connected yet</div>
        )}
      </div>

      {/* Session controls */}
      <div className="mt-4 pt-4 border-t border-gray-700" data-testid="session-controls">
        <button
          data-testid="invite-players-button"
          className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
          aria-label="Invite players to session"
        >
          Invite Players
        </button>
      </div>
    </div>
  );
};
